import { useTranslation } from "react-i18next";
import { Flag, Check } from "@phosphor-icons/react";

import type { WorkoutItem } from "../../planApi";
import { itemLoad, weekTotals, weekDelta } from "../../pages/planUtils";
import { getSportColor, isCalledOffStatus, isDoneStatus } from "../../workoutStatus";

/**
 * WeekLoadHero — hero таба План (W-REBUILD-2, мандат раздел 6.2):
 * «неделя как нагрузочный профиль».
 *
 * Один объект: бары TSS по дням (цвет = дисциплина, выполненное — ✓ и
 * приглушение, сегодня — подсветка, гонка — флаг) → строка итогов
 * недели (TSS · часы · км · дисциплины, честная математика план+факт)
 * → спарклайн 4 недель с дельтой к прошлой («на 12% тяжелее»).
 *
 * Пусто (нет тренировок на неделе) — рендерим осмысленную строку, не
 * нулевые бары.
 */

const BAR_MAX_H = 72;
const SPARK_H = 28;

export interface WeekLoadHeroDay {
  iso: string;
  label: string; // "пн"
  dayNum: number;
  items: WorkoutItem[];
  isToday: boolean;
  isRace: boolean;
}

export interface WeekLoadHeroProps {
  days: WeekLoadHeroDay[];
  /** Недельные суммы TSS, старые → новые; последний = текущая неделя. */
  weeklySums: number[];
  onDayClick?: (iso: string) => void;
  /** Открыть профиль нагрузки во весь экран. Без обработчика кнопки нет. */
  onExpand?: () => void;
}

export function WeekLoadHero({ days, weeklySums, onDayClick, onExpand }: WeekLoadHeroProps) {
  const { t } = useTranslation("plan");

  const allItems = days.flatMap((d) => d.items);
  const totals = weekTotals(allItems);
  const dayLoads = days.map((d) =>
    d.items.reduce((sum, w) => sum + (itemLoad(w) ?? 0), 0),
  );
  const maxLoad = Math.max(...dayLoads, 1);

  const currentTss = weeklySums.length ? weeklySums[weeklySums.length - 1] : totals.tss;
  const prevTss = weeklySums.length >= 2 ? weeklySums[weeklySums.length - 2] : 0;
  // Дельту показываем, только когда у недели известна ВСЯ нагрузка: иначе
  // сравнение говорит о полноте данных, а не о плане (см. weekDelta).
  const delta = weekDelta(currentTss, prevTss, totals.itemsWithoutLoad === 0);
  const sparkMax = Math.max(...weeklySums, 1);

  const hoursLabel =
    totals.hours >= 10 ? totals.hours.toFixed(0) : totals.hours.toFixed(1);

  const deltaWordLabel =
    delta.word === "harder"
      ? t("load_hero.delta_harder", { defaultValue: "неделя тяжелее" })
      : delta.word === "easier"
      ? t("load_hero.delta_easier", { defaultValue: "неделя легче" })
      : delta.word === "recovery"
      ? t("load_hero.delta_recovery", { defaultValue: "разгрузочная" })
      : delta.word === "same"
      ? t("load_hero.delta_same", { defaultValue: "как прошлая" })
      : null;

  return (
    <div data-testid="week-load-hero" className="indigo-card p-4 flex flex-col gap-3">
      {/* ── Бары по дням ── */}
      {allItems.length === 0 ? (
        <div data-testid="week-load-hero-empty" className="py-2">
          <div className="text-[13px] text-fg-secondary">
            {t("load_hero.empty", { defaultValue: "На этой неделе тренировок нет." })}
          </div>
        </div>
      ) : (
        <div className="flex items-end gap-1.5" style={{ height: BAR_MAX_H + 34 }}>
          {days.map((d, i) => {
            const load = dayLoads[i];
            // День, где все сессии отменены, не «недоделан» — их сняли.
            // Считаем по тем, что реально предстояли.
            const live = d.items.filter((w) => !isCalledOffStatus(w.status));
            const done = live.length > 0 && live.every((w) => isDoneStatus(w.status));
            const h =
              load > 0 ? Math.max(6, Math.round((load / maxLoad) * BAR_MAX_H)) : 0;
            const firstSport = d.items[0]?.discipline ?? null;
            const color = getSportColor(firstSport);
            return (
              <button
                key={d.iso}
                type="button"
                data-testid={`load-bar-${i}`}
                data-load={load}
                data-done={done ? "true" : "false"}
                onClick={onDayClick ? () => onDayClick(d.iso) : undefined}
                className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0"
              >
                {/* число TSS над баром — только когда есть нагрузка */}
                <span className="text-[9px] tabular-nums font-bold text-fg-secondary leading-none h-3">
                  {load > 0 ? load : ""}
                </span>
                <div className="w-full flex items-end justify-center" style={{ height: BAR_MAX_H }}>
                  {d.items.length === 0 ? (
                    <div className="w-full max-w-[26px] h-[3px] rounded-sm bg-bg-border/70" />
                  ) : (
                    <div
                      className="w-full max-w-[26px] rounded-t-[4px]"
                      style={{
                        height: h || 6,
                        backgroundColor: color,
                        opacity: done ? 0.45 : 1,
                      }}
                    />
                  )}
                </div>
                <span
                  className={[
                    "text-[9px] uppercase tracking-[0.14em] font-bold leading-none",
                    d.isToday ? "text-accent" : d.isRace ? "text-ok" : "text-fg-muted",
                  ].join(" ")}
                >
                  {d.label}
                </span>
                <span className="h-3 flex items-center">
                  {d.isRace ? (
                    <Flag size={10} className="text-ok" weight="fill" aria-hidden />
                  ) : done ? (
                    <Check size={10} className="text-ok" aria-hidden />
                  ) : d.isToday ? (
                    <span className="w-1 h-1 rounded-full bg-accent inline-block" />
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Итоги недели ── */}
      <div
        data-testid="week-load-totals"
        className="flex items-baseline gap-3 flex-wrap border-t border-bg-border/60 pt-3"
      >
        <span className="flex items-baseline gap-1">
          <span className="text-[17px] font-bold tabular-nums text-fg-primary leading-none">
            {totals.tss}
          </span>
          <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">TSS</span>
        </span>
        <span className="flex items-baseline gap-1">
          <span className="text-[17px] font-bold tabular-nums text-fg-primary leading-none">
            {hoursLabel}
          </span>
          <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">
            {t("plan_sidebar.hours_unit", { defaultValue: "ч" })}
          </span>
        </span>
        {totals.km > 0 && (
          <span className="flex items-baseline gap-1">
            <span className="text-[17px] font-bold tabular-nums text-fg-primary leading-none">
              {Math.round(totals.km)}
            </span>
            <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">
              {t("plan_sidebar.km_unit", { defaultValue: "км" })}
            </span>
          </span>
        )}
        {totals.disciplines > 1 && (
          <span className="text-[11px] text-fg-secondary ml-auto">
            {t("load_hero.disciplines", {
              defaultValue: "{{n}} дисципл.",
              n: totals.disciplines,
            })}
          </span>
        )}
      </div>

      {/* Часы и километры считают все тренировки недели, TSS — только те, у
          кого известна нагрузка (у импортных планов она пуста). Раз числа
          стоят в одной строке, разницу называем, а не оставляем читателю. */}
      {totals.itemsWithoutLoad > 0 && (
        <p
          data-testid="week-load-partial-note"
          className="text-[10px] text-fg-muted -mt-1.5"
        >
          {t("load_hero.tss_partial", {
            defaultValue:
              "TSS без {{n}} тренировок: у них нагрузка не задана. Часы и км — за всю неделю.",
            n: totals.itemsWithoutLoad,
          })}
        </p>
      )}

      {/* ── Спарклайн 4 недель + дельта ── */}
      {weeklySums.some((v) => v > 0) && (
        <div data-testid="week-load-spark" className="flex items-center gap-3">
          <div className="flex items-end gap-1" style={{ height: SPARK_H }}>
            {weeklySums.map((v, i) => {
              const isCurrent = i === weeklySums.length - 1;
              return (
                <div
                  key={i}
                  data-testid={`spark-week-${i}`}
                  className="w-[14px] rounded-t-[3px]"
                  style={{
                    height: Math.max(3, Math.round((v / sparkMax) * SPARK_H)),
                    backgroundColor: isCurrent
                      ? "rgb(var(--accent, 155 165 255))"
                      : "var(--fg-muted)",
                  }}
                />
              );
            })}
          </div>
          <div className="min-w-0 flex-1 text-[11.5px] text-fg-secondary leading-snug">
            {delta.pct != null ? (
              <span data-testid="week-load-delta">
                {t("load_hero.delta_line", {
                  defaultValue: "к прошлой: {{pct}}%",
                  pct: `${delta.pct > 0 ? "+" : ""}${delta.pct}`,
                })}
                {deltaWordLabel && (
                  <span className="text-fg-primary font-semibold"> · {deltaWordLabel}</span>
                )}
              </span>
            ) : (
              <span data-testid="week-load-delta-none">
                {t("load_hero.delta_no_prev", { defaultValue: "прошлая неделя без нагрузки" })}
              </span>
            )}
          </div>
          {/* Вход в развёрнутый вид. Четыре столбика по четырнадцать
              пикселей не могут показать структуру блока — их ширины
              хватает ровно на «выше или ниже прошлой». */}
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              data-testid="week-load-expand"
              className="shrink-0 px-2.5 h-8 rounded-md text-[11px] font-bold text-fg-secondary hover:text-fg-primary bg-bg-card"
            >
              {t("load_hero.expand", { defaultValue: "шире" })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
